import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { getCommerceId } from '@/lib/ownership';
import { getProductImage } from '@/lib/productMedia';
import { menuRoleLabel } from '@/lib/menuRole';
import { useHomeConfig } from '@/hooks/useHomeConfig';
import {
  WEEKDAYS,
  DEFAULT_DAY_HOURS,
  normalizeOpeningHours,
  getOpenState,
  type OpeningHours,
} from '@/lib/restaurantHours';

// ─────────────────────────────────────────────────────────────
// Dashboard — espace « Restauration » (visible seulement quand
// le type de compte du marchand est Restauration, typecompte = 2).
//   • Horaires d'ouverture (déclenche la bannière « fermé » publique)
//   • Promo du moment (plat mis en avant sur la page d'accueil)
// ─────────────────────────────────────────────────────────────

interface ProductRow {
  id: number;
  name: string;
  media: unknown;
  menu_role?: string | null;
}

export default function RestaurantDashboardPage() {
  const { user } = useAuth();
  const merchantId = user ? getCommerceId(user) : null;
  const { config, loading, saveOpeningHours, setPromoProduct } = useHomeConfig(merchantId);

  const [hours, setHours] = useState<OpeningHours>(normalizeOpeningHours(null));
  const [savingHours, setSavingHours] = useState(false);
  const [hoursSaved, setHoursSaved] = useState(false);

  const [products, setProducts] = useState<ProductRow[]>([]);
  const [promoId, setPromoId] = useState<number | null>(null);
  const [savingPromo, setSavingPromo] = useState(false);
  const [promoSaved, setPromoSaved] = useState(false);

  useEffect(() => {
    setHours(normalizeOpeningHours(config.openingHours));
    setPromoId(config.promoProductId);
  }, [config.openingHours, config.promoProductId]);

  useEffect(() => {
    if (!merchantId) return;
    let cancelled = false;
    supabase
      .from('product_items')
      .select('id, name, media, menu_role')
      .eq('idcommerce', merchantId)
      .order('created_at', { ascending: false })
      .limit(100)
      .then(({ data }) => {
        if (!cancelled) setProducts((data as ProductRow[]) || []);
      })
      .catch(() => {
        if (!cancelled) setProducts([]);
      });
    return () => { cancelled = true; };
  }, [merchantId]);

  const updateDay = (key: string, patch: Partial<{ closed: boolean; open: string; close: string }>) => {
    setHours((prev) => ({ ...prev, [key]: { ...(prev[key] || DEFAULT_DAY_HOURS), ...patch } }));
  };

  const handleSaveHours = async () => {
    setSavingHours(true);
    setHoursSaved(false);
    const ok = await saveOpeningHours(hours);
    setSavingHours(false);
    if (ok) {
      setHoursSaved(true);
      setTimeout(() => setHoursSaved(false), 2500);
    }
  };

  const handleSavePromo = async () => {
    setSavingPromo(true);
    setPromoSaved(false);
    const ok = await setPromoProduct(promoId);
    setSavingPromo(false);
    if (ok) {
      setPromoSaved(true);
      setTimeout(() => setPromoSaved(false), 2500);
    }
  };

  const openState = getOpenState(hours, new Date());

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-3xl">
      <div className="mb-6">
        <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
          <i className="ri-restaurant-2-line mr-2 text-primary-500"></i>
          Espace Restaurant
        </h2>
        <p className="text-sm text-foreground-500 mt-1">
          Gérez vos horaires d'ouverture et la promo du moment affichée sur votre site.
        </p>
      </div>

      {/* Statut actuel */}
      <div
        className={`mb-6 flex items-center gap-3 px-4 py-3 rounded-xl border ${
          openState.open ? 'bg-accent-50 border-accent-200' : 'bg-primary-50 border-primary-200'
        }`}
      >
        <span
          className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
            openState.open ? 'bg-accent-100 text-accent-600' : 'bg-primary-100 text-primary-600'
          }`}
        >
          <i className={openState.open ? 'ri-store-2-line text-lg' : 'ri-door-closed-line text-lg'}></i>
        </span>
        <div>
          <p className="text-sm font-semibold text-foreground-900">
            {openState.open ? 'Actuellement ouvert' : 'Actuellement fermé'}
          </p>
          <p className="text-xs text-foreground-500">
            {openState.todayLabel ? `Horaires du jour : ${openState.todayLabel}` : 'Aucun horaire configuré — le site reste ouvert.'}
          </p>
        </div>
      </div>

      {/* Horaires d'ouverture */}
      <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 md:p-5 mb-6">
        <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-1.5 mb-1">
          <i className="ri-time-line text-primary-500"></i>
          Horaires d'ouverture
        </h3>
        <p className="text-xs text-foreground-500 mb-4">
          En dehors de ces horaires, une grande bannière « Restaurant fermé » s'affiche sur votre site.
        </p>

        <div className="space-y-2">
          {WEEKDAYS.map((day) => {
            const entry = hours[day.key] || DEFAULT_DAY_HOURS;
            return (
              <div
                key={day.key}
                className="flex flex-wrap items-center gap-3 py-2 border-b border-background-200/60 last:border-0"
              >
                <span className="w-24 text-sm font-medium text-foreground-800">{day.label}</span>
                <button
                  type="button"
                  onClick={() => updateDay(day.key, { closed: !entry.closed })}
                  className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
                    entry.closed
                      ? 'bg-background-100 text-foreground-500 hover:bg-background-200/70'
                      : 'bg-accent-50 text-accent-700'
                  }`}
                >
                  {entry.closed ? 'Fermé' : 'Ouvert'}
                </button>
                {!entry.closed && (
                  <div className="flex items-center gap-2">
                    <input
                      type="time"
                      value={entry.open}
                      onChange={(e) => updateDay(day.key, { open: e.target.value })}
                      className="px-2 py-1.5 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                    />
                    <span className="text-foreground-400 text-sm">→</span>
                    <input
                      type="time"
                      value={entry.close}
                      onChange={(e) => updateDay(day.key, { close: e.target.value })}
                      className="px-2 py-1.5 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex items-center gap-3 mt-5 pt-4 border-t border-background-200/70">
          <button
            onClick={handleSaveHours}
            disabled={savingHours}
            className="flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer"
          >
            {savingHours ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Enregistrement...
              </>
            ) : (
              <>
                <i className="ri-check-line"></i>
                Enregistrer les horaires
              </>
            )}
          </button>
          {hoursSaved && (
            <span className="text-sm text-accent-600 flex items-center gap-1">
              <i className="ri-checkbox-circle-line"></i>
              Enregistré !
            </span>
          )}
        </div>
      </div>

      {/* Promo du moment */}
      <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 md:p-5">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-1.5">
            <i className="ri-fire-line text-accent-500"></i>
            Promo du moment
          </h3>
          {promoId != null && (
            <button
              type="button"
              onClick={() => setPromoId(null)}
              className="text-xs text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              Retirer la promo
            </button>
          )}
        </div>
        <p className="text-xs text-foreground-500 mb-4">
          Choisissez le plat mis en avant dans le bloc « Promo du moment » de votre page d'accueil.
        </p>

        {products.length === 0 ? (
          <div className="text-sm text-foreground-400 flex items-center gap-2 py-3">
            <i className="ri-information-line"></i>
            Aucun plat. Ajoutez-en depuis le dashboard Produits.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-96 overflow-y-auto pr-1">
            {products.map((p) => {
              const active = promoId === p.id;
              const img = getProductImage(p.media);
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPromoId(active ? null : p.id)}
                  className={`flex items-center gap-3 p-2.5 rounded-lg border text-left transition-colors cursor-pointer ${
                    active ? 'bg-accent-50/70 border-accent-300' : 'bg-background-50 border-background-200/70 hover:bg-background-100'
                  }`}
                >
                  <div className="w-10 h-10 rounded-md bg-background-100 overflow-hidden flex-shrink-0">
                    {img ? (
                      <img
                        src={img}
                        alt={p.name}
                        className="w-full h-full object-cover"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-foreground-300">
                        <i className="ri-image-line"></i>
                      </div>
                    )}
                  </div>
                  <span className="text-sm font-medium text-foreground-800 flex-1 min-w-0 truncate">{p.name}</span>
                  {menuRoleLabel(p.menu_role) && (
                    <span className="px-2 py-0.5 bg-accent-50 text-accent-700 rounded-full text-[10px] font-medium whitespace-nowrap flex-shrink-0">
                      {menuRoleLabel(p.menu_role)}
                    </span>
                  )}
                  <i className={active ? 'ri-checkbox-circle-fill text-accent-500' : 'ri-checkbox-blank-circle-line text-foreground-300'}></i>
                </button>
              );
            })}
          </div>
        )}

        <div className="flex items-center gap-3 mt-5 pt-4 border-t border-background-200/70">
          <button
            onClick={handleSavePromo}
            disabled={savingPromo}
            className="flex items-center gap-2 px-5 py-2.5 bg-accent-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-accent-600 disabled:opacity-50 transition-colors cursor-pointer"
          >
            {savingPromo ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Enregistrement...
              </>
            ) : (
              <>
                <i className="ri-check-line"></i>
                Enregistrer la promo
              </>
            )}
          </button>
          {promoSaved && (
            <span className="text-sm text-accent-600 flex items-center gap-1">
              <i className="ri-checkbox-circle-line"></i>
              Enregistré !
            </span>
          )}
        </div>
      </div>
    </div>
  );
}